/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/Avatar';
import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { ErrorMessage } from '@atlaskit/form';
import InlineEdit from '@atlaskit/inline-edit';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import Textfield from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';
import { useProfileController } from '@atlassian/conversation-assistant-store/controllers/profile';

import { ActionCardFooter } from '../../../common/ui/action-card-wrapper';
import { FocusAskCreateModal } from '../ask-create-modal';
import { CommentField } from '../fields/comment/CommentField';
import { ImpactedWorkField } from '../fields/impacted-work';
import { type ImpactedWorkResolutionStatus } from '../fields/impacted-work/useImpactedWorkResolution';
import { FocusAskLinksSection } from '../fields/links/FocusAskLinksSection';
import { MAX_ASK_NAME_LENGTH } from '../fields/name';
import { PriorityField } from '../fields/priority';
import { TeamPickerField } from '../fields/team';
import { UserPickerField } from '../fields/user';
import { FocusAskActionCardWrapper } from '../FocusAskActionCardWrapper';
import messages from '../messages';
import { normalizeFocusAskPayload } from '../normalize-focus-ask-payload';
import type { FocusAskFormData, FocusAskUserData } from '../types';

import { CompactDetailValue } from './components/CompactDetailValue';
import { DetailRow } from './components/DetailRow';
import { InlineEditorSection } from './components/InlineEditor';
import { PreviewSection } from './components/Section';

type FocusAskAction = {
	invocationId: string;
	status?: string;
	data: unknown;
};

const styles = cssMap({
	footer: {
		display: 'flex',
		justifyContent: 'flex-end',
		gap: token('space.100'),
	},
	fieldsWrapper: {
		width: '100%',
		minWidth: '0',
	},
	commentInput: {
		flexGrow: 1,
		minWidth: '0',
	},
});

export const FocusAskCreateRenderer = ({
	action,
	cloudId,
	orgId,
	readonly = false,
	isActionFinished = false,
	isSubmitting = false,
	onConfirm,
}: {
	action: FocusAskAction;
	cloudId: string;
	orgId: string;
	readonly?: boolean;
	isActionFinished?: boolean;
	isSubmitting?: boolean;
	onConfirm?: (action: FocusAskAction, formData: FocusAskFormData) => void | Promise<void>;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const [formData, setFormData] = useState<FocusAskFormData>(() => {
		const data = normalizeFocusAskPayload(action.data);
		return {
			name: data.name,
			description: data.description ?? '',
			justification: data.justification ?? '',
			targetDate: data.targetDate,
			commentData: data.commentData,
			links: data.links,
			submitter: data.submitter,
			owner: data.owner,
			submittingTeam: data.submittingTeam,
			receivingTeam: data.receivingTeam,
			priority: data.priority,
			impactedWork: data.impactedWork
				? { id: data?.impactedWork?.id ?? '', link: data?.impactedWork?.link ?? '' }
				: null,
		};
	});

	const currentUser = useProfileController();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isLinksEditorOpen, setIsLinksEditorOpen] = useState(false);
	const [impactedWorkStatus, setImpactedWorkStatus] = useState<ImpactedWorkResolutionStatus>(
		formData.impactedWork ? 'resolving' : 'idle',
	);
	const isCardReadOnly = readonly || isActionFinished;
	const buttonsDisabled = isCardReadOnly || isSubmitting;
	const trimmedName = formData.name.trim();
	const isImpactedWorkBlockingCreate =
		impactedWorkStatus !== 'idle' && impactedWorkStatus !== 'resolved';

	const isCreateButtonDisabled =
		buttonsDisabled ||
		!trimmedName ||
		!formData.submitter?.id?.trim() ||
		formData.name.length > MAX_ASK_NAME_LENGTH ||
		isLinksEditorOpen ||
		isImpactedWorkBlockingCreate;

	const handleConfirm = useCallback(() => {
		if (isCreateButtonDisabled) {
			return;
		}
		onConfirm?.(action, { ...formData, name: trimmedName });
	}, [onConfirm, action, formData, trimmedName, isCreateButtonDisabled]);

	const handleEditDetails = useCallback(() => {
		if (buttonsDisabled) {
			return;
		}
		setIsModalOpen(true);
	}, [buttonsDisabled]);

	const handleModalClose = useCallback(() => {
		setIsModalOpen(false);
	}, []);

	const handleModalSubmit = useCallback(
		(updatedData: FocusAskFormData) => {
			if (buttonsDisabled) {
				setIsModalOpen(false);
				return;
			}
			setFormData(updatedData);
			setIsModalOpen(false);
			setIsLinksEditorOpen(false);
			setImpactedWorkStatus(updatedData.impactedWork ? 'resolving' : 'idle');
			onConfirm?.(action, updatedData);
		},
		[onConfirm, action, buttonsDisabled],
	);

	const handleNameChange = useCallback((value: string) => {
		setFormData((prev) => ({ ...prev, name: value }));
	}, []);

	const handleDescriptionChange = useCallback((value: string) => {
		setFormData((prev) => ({ ...prev, description: value }));
	}, []);

	const handleJustificationChange = useCallback((value: string) => {
		setFormData((prev) => ({ ...prev, justification: value }));
	}, []);

	const handleSubmitterChange = useCallback((user: FocusAskUserData | null) => {
		setFormData((prev) => ({ ...prev, submitter: user }));
	}, []);

	const handleOwnerChange = useCallback((user: FocusAskUserData | null) => {
		setFormData((prev) => ({ ...prev, owner: user }));
	}, []);

	const handleFormDataUpdate = useCallback(
		<K extends keyof FocusAskFormData>(field: K, value: FocusAskFormData[K]) => {
			setFormData((prev) => ({ ...prev, [field]: value }));
		},
		[],
	);

	const canEditLinks = !buttonsDisabled;
	const shouldShowImpactedWorkField =
		Boolean(formData.impactedWork) || impactedWorkStatus !== 'idle';

	return (
		<FocusAskActionCardWrapper>
			<Stack space="space.200" xcss={styles.fieldsWrapper}>
				{isCardReadOnly ? (
					<Text size="large" weight="bold">
						{formData.name}
					</Text>
				) : (
					<InlineEdit<string>
						isRequired
						defaultValue={formData.name}
						editView={(fieldProps) => {
							const isMissing = !fieldProps.value.trim();
							const isTooLong = fieldProps.value.length > MAX_ASK_NAME_LENGTH;

							return (
								<>
									<Textfield
										{...fieldProps}
										isCompact
										maxLength={MAX_ASK_NAME_LENGTH}
										isInvalid={isMissing || isTooLong}
									/>
									{isMissing && (
										<ErrorMessage>{formatMessage(messages.bulkMissingInfo)}</ErrorMessage>
									)}
									{!isMissing && isTooLong && (
										<ErrorMessage>
											{formatMessage(messages.nameFieldTooLong, {
												maxLength: MAX_ASK_NAME_LENGTH,
											})}
										</ErrorMessage>
									)}
								</>
							);
						}}
						readView={() => (
							<Text size="large" weight="bold">
								{formData.name}
							</Text>
						)}
						onConfirm={(value) => {
							const nextName = value.trim();
							if (nextName && value.length <= MAX_ASK_NAME_LENGTH) {
								handleNameChange(nextName);
							}
						}}
						readViewFitContainerWidth
					/>
				)}

				<PreviewSection title={formatMessage(messages.detailsSectionLabel)}>
					<Stack space="space.0">
						<DetailRow label={formatMessage(messages.submitterFieldLabel)}>
							<CompactDetailValue appearance="control">
								<UserPickerField
									name="submitter"
									label={messages.submitterFieldLabel}
									placeholder={messages.submitterPickerPlaceholder}
									cloudId={cloudId}
									value={formData.submitter}
									onChange={handleSubmitterChange}
									isRequired
									fieldId="submitter-preview"
									isDisabled={buttonsDisabled}
									isClearable={false}
									hideLabel
									isCompact
									appearance="subtle"
								/>
							</CompactDetailValue>
						</DetailRow>

						<DetailRow label={formatMessage(messages.receiverFieldLabel)}>
							<CompactDetailValue appearance="control">
								<UserPickerField
									name="owner"
									label={messages.receiverFieldLabel}
									placeholder={messages.receiverPickerPlaceholder}
									cloudId={cloudId}
									value={formData.owner}
									onChange={handleOwnerChange}
									fieldId="owner-preview"
									isDisabled={buttonsDisabled}
									hideLabel
									isCompact
									appearance="subtle"
								/>
							</CompactDetailValue>
						</DetailRow>

						<DetailRow label={formatMessage(messages.priorityFieldLabel)}>
							<CompactDetailValue appearance="control">
								<PriorityField
									value={formData.priority}
									onChange={(value) => handleFormDataUpdate('priority', value)}
									hideLabel
									isCompact
									appearance="subtle"
									isDisabled={buttonsDisabled}
								/>
							</CompactDetailValue>
						</DetailRow>

						{formData.submittingTeam && (
							<DetailRow label={formatMessage(messages.submittingTeamFieldLabel)}>
								<CompactDetailValue appearance="control">
									<TeamPickerField
										name="submittingTeam"
										label={messages.submittingTeamFieldLabel}
										placeholder={messages.submittingTeamPickerPlaceholder}
										cloudId={cloudId}
										orgId={orgId}
										value={formData.submittingTeam}
										onChange={(team) => handleFormDataUpdate('submittingTeam', team)}
										fieldId="submitting-team-preview"
										isDisabled={buttonsDisabled}
										hideLabel
										isCompact
										appearance="subtle"
									/>
								</CompactDetailValue>
							</DetailRow>
						)}

						{formData.receivingTeam && (
							<DetailRow label={formatMessage(messages.receivingTeamFieldLabel)}>
								<CompactDetailValue appearance="control">
									<TeamPickerField
										name="receivingTeam"
										label={messages.receivingTeamFieldLabel}
										placeholder={messages.receivingTeamPickerPlaceholder}
										cloudId={cloudId}
										orgId={orgId}
										value={formData.receivingTeam}
										onChange={(team) => handleFormDataUpdate('receivingTeam', team)}
										fieldId="receiving-team-preview"
										isDisabled={buttonsDisabled}
										hideLabel
										isCompact
										appearance="subtle"
									/>
								</CompactDetailValue>
							</DetailRow>
						)}
					</Stack>
				</PreviewSection>

				<InlineEditorSection
					title={formatMessage(messages.descriptionFieldLabel)}
					placeholder={formatMessage(messages.descriptionEditorPlaceholder)}
					value={formData.description}
					onConfirm={handleDescriptionChange}
					isDisabled={buttonsDisabled}
				/>

				<InlineEditorSection
					title={formatMessage(messages.justificationFieldLabel)}
					placeholder={formatMessage(messages.justificationEditorPlaceholder)}
					value={formData.justification}
					onConfirm={handleJustificationChange}
					isDisabled={buttonsDisabled}
				/>

				<FocusAskLinksSection
					links={formData.links}
					onChange={(nextLinks) => handleFormDataUpdate('links', nextLinks)}
					isEditorOpen={isLinksEditorOpen}
					onEditorOpenChange={setIsLinksEditorOpen}
					canEdit={canEditLinks}
					isDisabled={buttonsDisabled}
				/>

				{shouldShowImpactedWorkField && (
					<PreviewSection title={formatMessage(messages.impactedItemFieldLabel)}>
						<ImpactedWorkField
							value={formData.impactedWork}
							cloudId={cloudId}
							onValueChange={(value) => handleFormDataUpdate('impactedWork', value)}
							onResolutionStatusChange={setImpactedWorkStatus}
							isDisabled={buttonsDisabled}
							hideLabel
						/>
					</PreviewSection>
				)}

				<PreviewSection title={formatMessage(messages.commentsSectionLabel)}>
					<Inline space="space.100" alignBlock="center">
						<Avatar name={currentUser.displayName} src={currentUser.avatarUrl} size="small" />
						<Box xcss={styles.commentInput}>
							<CommentField
								value={formData.commentData}
								onChange={(value) => handleFormDataUpdate('commentData', value)}
								fieldId="comment-preview"
								hideLabel
								isCompact
								isDisabled={buttonsDisabled}
							/>
						</Box>
					</Inline>
				</PreviewSection>
			</Stack>

			{!isActionFinished && (
				<ActionCardFooter>
					<Box xcss={styles.footer}>
						<Button appearance="subtle" onClick={handleEditDetails} isDisabled={buttonsDisabled}>
							{formatMessage(messages.refineButton)}
						</Button>
						<Button
							appearance="primary"
							onClick={handleConfirm}
							isDisabled={isCreateButtonDisabled}
							isLoading={isSubmitting}
						>
							{formatMessage(messages.createButton)}
						</Button>
					</Box>
				</ActionCardFooter>
			)}

			<FocusAskCreateModal
				key={isModalOpen ? 'focus-ask-modal-open' : 'focus-ask-modal-closed'}
				data={isModalOpen && !buttonsDisabled ? formData : null}
				cloudId={cloudId}
				orgId={orgId}
				onClose={handleModalClose}
				onSubmit={handleModalSubmit}
			/>
		</FocusAskActionCardWrapper>
	);
};
